import { SettingsPermissions } from "@redotech/account-common/permissions";
import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import DownArrowIcon from "@redotech/redo-icons/arrow-down.svg";
import UpArrowIcon from "@redotech/redo-icons/arrow-up.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { newChoiceState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { RequiredMessage } from "@redotech/redo-return-ui/flows/required-message";
import {
  ChoiceState,
  MultipleChoiceState,
} from "@redotech/redo-return-ui/flows/return-flow/multiple-choice";
import * as multipleChoiceCss from "@redotech/redo-return-ui/flows/return-flow/multiple-choice.module.css";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import Info from "@redotech/redo-web/icon-old/info.svg";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, ReactNode, useContext, useMemo } from "react";
import { useFieldEditable } from "../flow-editor/flow-readonly-context";
import {
  descriptionFieldSpec,
  optionNameFieldSpec,
  promptFieldSpec,
} from "./translations/field-specs";
import { FlowFieldTranslations } from "./translations/flow-field-translations";
import { StepTranslationLanguages } from "./translations/step-translation-languages";
import {
  stepElementIdsOf,
  StepTranslationElement,
} from "./translations/step-translations";
import { TranslatableTextareaField } from "./translations/translatable-textarea-field";

interface AdditionalAiInfoProps {
  canEditReturnsSettings: boolean;
  setAdditionalAiInfo: (additionalAiInfo: string | undefined) => void;
  currentAdditionalAiInfo: string | undefined;
  collapsedTitle?: string;
  tooltip?: ReactNode;
}

export const AdditionalAiInfo = memo(function AdditionalAiInfo({
  canEditReturnsSettings,
  setAdditionalAiInfo,
  currentAdditionalAiInfo,
  collapsedTitle,
}: AdditionalAiInfoProps) {
  const { t } = useTranslation();
  return (
    <RedoCollapseSubsection title={collapsedTitle ?? t("Advanced options")}>
      <Flex dir="column">
        <Flex align="center">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Additional context for variant exchange AI")}
          </Text>
          <Tooltip
            enterTouchDelay={0}
            placement="left"
            title={t(
              "Provide additional information to help AI make better exchange suggestions",
            )}
          >
            <RedoButton
              aria-label={t("More information")}
              hierarchy="tertiary"
              IconLeading={Info}
              onClick={(event) => {
                event.stopPropagation();
              }}
              size="md"
              theme="normal"
            />
          </Tooltip>
        </Flex>
        <RedoTextareaInputField
          readOnly={!canEditReturnsSettings}
          setValue={setAdditionalAiInfo}
          style={{ minWidth: "unset" }}
          value={currentAdditionalAiInfo || ""}
        />
      </Flex>
    </RedoCollapseSubsection>
  );
});

export const MultipleChoiceDetails = memo(function MultipleChoiceDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<MultipleChoiceState>) {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const canEditReturnsSettings = useFieldEditable(
    useHasPermission(SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS),
  );
  const handleAdd = useHandler(() =>
    setState((state) => ({
      ...state,
      choices: [...state.choices, newChoiceState()],
    })),
  );
  const handlePrompt = useHandler((prompt: string) =>
    setState((state) => ({ ...state, prompt })),
  );
  const handleDescription = useHandler((description: string) => {
    setState((state) => ({ ...state, description }));
  });
  const handleShuffle = useHandler((shuffle: boolean) =>
    setState((state) => ({ ...state, shuffle })),
  );
  const handleFocus = useHandler((focused: boolean) => {
    if (!focused) {
      setState((state) => ({ ...state, attempted: true }));
    }
  });
  const handleAdditionalAiInfo = useHandler(
    (additionalAiInfo: string | undefined) =>
      setState((state) => ({ ...state, additionalAiInfo })),
  );
  const isRedoUser =
    (user?.email && /@getredo\.com/.test(user?.email)) || false;

  const translationElements = useMemo<StepTranslationElement[]>(
    () => [
      { elementId: state.stepUUID, field: promptFieldSpec(state.prompt) },
      {
        elementId: state.stepUUID,
        field: descriptionFieldSpec(state.description),
      },
      ...state.choices.map((choice, index) => ({
        elementId: choice.optionUUID,
        field: optionNameFieldSpec(index, choice.name),
      })),
    ],
    [state.stepUUID, state.prompt, state.description, state.choices],
  );
  const stepElementIds = useMemo(
    () => stepElementIdsOf(translationElements),
    [translationElements],
  );

  return (
    <>
      <RedoToggle
        description={t(
          "This will shuffle the order that return reasons are displayed for each individual order",
        )}
        disabled={!canEditReturnsSettings}
        label={t("Shuffle options")}
        setValue={handleShuffle}
        value={state.shuffle ?? false}
      />
      <StepTranslationLanguages
        canEdit={canEditReturnsSettings}
        elements={translationElements}
        flowType={flowType}
      />
      <TranslatableTextareaField
        canEdit={canEditReturnsSettings}
        elementId={state.stepUUID}
        errorText={state.attempted && !state.prompt ? t("Required") : undefined}
        field={promptFieldSpec(state.prompt)}
        flowType={flowType}
        setValue={handlePrompt}
        stepElementIds={stepElementIds}
      />
      <TranslatableTextareaField
        canEdit={canEditReturnsSettings}
        elementId={state.stepUUID}
        field={descriptionFieldSpec(state.description)}
        flowType={flowType}
        helperText={t("Additional help or explanation. Can include HTML.")}
        setValue={handleDescription}
        stepElementIds={stepElementIds}
      />
      {isRedoUser ? (
        <AdditionalAiInfo
          canEditReturnsSettings={canEditReturnsSettings}
          currentAdditionalAiInfo={state.additionalAiInfo}
          setAdditionalAiInfo={handleAdditionalAiInfo}
        />
      ) : null}
      <Flex dir="column" gap="md">
        <IterableMap items={state.choices} keyFn={(_, index) => index}>
          {(choice, index) => (
            <StateChoiceComponent
              canEditReturnsSettings={canEditReturnsSettings}
              choice={choice}
              flowType={flowType}
              handleFocus={handleFocus}
              index={index}
              isRedoUser={isRedoUser}
              reason={state.reason}
              setState={setState}
              state={state}
              stepElementIds={stepElementIds}
              stepSelect={stepSelect}
            />
          )}
        </IterableMap>
      </Flex>
      <Flex align="flex-start" pt="md">
        <RedoButton
          disabled={!canEditReturnsSettings}
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={handleAdd}
          size="sm"
          text={t("Add option")}
        />
      </Flex>
      {!state.choices.length && (
        <RequiredMessage>{t("Must have at least one option")}</RequiredMessage>
      )}
    </>
  );
});

const StateChoiceComponent = memo(function StateChoiceComponent({
  state,
  setState,
  stepSelect,
  handleFocus,
  canEditReturnsSettings,
  choice,
  index,
  reason,
  isRedoUser,
  flowType,
  stepElementIds,
}: {
  state: StepTypeDetailsProps<MultipleChoiceState>["state"];
  setState: StepTypeDetailsProps<MultipleChoiceState>["setState"];
  stepSelect: StepTypeDetailsProps<MultipleChoiceState>["stepSelect"];
  handleFocus(focused: boolean): void;
  canEditReturnsSettings: boolean;
  choice: ChoiceState;
  index: number;
  reason: boolean;
  isRedoUser: boolean;
  flowType: StepTypeDetailsProps<MultipleChoiceState>["flowType"];
  stepElementIds: readonly string[];
}) {
  const { t } = useTranslation();
  const setName = useHandler((name: string) =>
    setState((state) =>
      produce(state, (state) => {
        const currentChoice = state.choices[index];
        if (currentChoice) {
          currentChoice.name = name;
        }
      }),
    ),
  );
  const setAdditionalAiInfo = useHandler(
    (additionalAiInfo: string | undefined) =>
      setState((state) =>
        produce(state, (state) => {
          const currentChoice = state.choices[index];
          if (currentChoice) {
            currentChoice.additionalAiInfo = additionalAiInfo;
          }
        }),
      ),
  );
  const setCode = useHandler((code: string | undefined) =>
    setState((state) =>
      produce(state, (state) => {
        const currentChoice = state.choices[index];
        if (currentChoice) {
          currentChoice.code = code;
        }
      }),
    ),
  );
  const setStep = useHandler((next: StepId | undefined) =>
    setState((state) =>
      produce(state, (state) => {
        const currentChoice = state.choices[index];
        if (currentChoice) {
          currentChoice.next = next;
        }
      }),
    ),
  );
  const handleUp = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index - 1, 0, ...choices);
      }),
    );
  });
  const handleDown = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index + 1, 0, ...choices);
      }),
    );
  });
  const handleDelete = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        state.choices.splice(index, 1);
      }),
    );
  });
  return (
    <Flex className={multipleChoiceCss.optionCard} dir="column">
      <Flex align="center" gap="xs" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {t("Option #{{optionNumber}}", { optionNumber: index + 1 })}
        </Text>
        {canEditReturnsSettings && (
          <Flex>
            <RedoButton
              aria-label={t("Move up")}
              disabled={!index}
              hierarchy="tertiary"
              IconLeading={UpArrowIcon}
              onClick={(event) => {
                handleUp();
                event.stopPropagation();
              }}
              size="sm"
              theme="normal"
            />
            <RedoButton
              aria-label={t("Move down")}
              disabled={index === state.choices.length - 1}
              hierarchy="tertiary"
              IconLeading={DownArrowIcon}
              onClick={(event) => {
                handleDown();
                event.stopPropagation();
              }}
              size="sm"
              theme="normal"
            />
            <RedoButton
              aria-label={t("Delete option")}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={(event) => {
                handleDelete();
                event.stopPropagation();
              }}
              size="sm"
              theme="destructive"
            />
          </Flex>
        )}
      </Flex>
      <RedoTextInput
        errorText={state.attempted && !choice.name ? t("Required") : undefined}
        label={t("Return reason")}
        onBlur={(event) => {
          if (!choice.name) {
            handleFocus(false);
          }
        }}
        setValue={setName}
        state={canEditReturnsSettings ? "default" : "readonly"}
        value={choice.name}
      />
      <FlowFieldTranslations
        canEdit={canEditReturnsSettings}
        elementId={choice.optionUUID}
        field={optionNameFieldSpec(index, choice.name)}
        flowType={flowType}
        stepElementIds={stepElementIds}
      />
      {reason && (
        <RedoTextInput
          label={t("Reason code")}
          onBlur={(event) => {
            if (!choice.code) {
              handleFocus(false);
            }
          }}
          setValue={setCode}
          state={canEditReturnsSettings ? "default" : "readonly"}
          trailingActions={
            <Tooltip
              enterTouchDelay={0}
              placement="top"
              title={t(
                "{{brandName}} will provide reason codes in api responses and webhook payloads",
                { brandName: "Redo" },
              )}
            >
              <RedoButton
                aria-label={t("More information")}
                hierarchy="tertiary"
                IconLeading={Info}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                size="md"
                theme="normal"
              />
            </Tooltip>
          }
          value={choice.code || ""}
        />
      )}

      <Text fontSize="xs" textColor="secondary">
        {t("Next step #{{stepNumber}}", { stepNumber: index + 1 })}
      </Text>
      {state.attempted && !choice.next && (
        <Text fontSize="xs" fontWeight="regular" textColor="error">
          {t("Required")}
        </Text>
      )}
      {stepSelect({
        value: choice.next,
        handleIndex: index,
        valueChange: setStep,
      })}
      {isRedoUser ? (
        <AdditionalAiInfo
          canEditReturnsSettings={canEditReturnsSettings}
          currentAdditionalAiInfo={choice.additionalAiInfo}
          setAdditionalAiInfo={setAdditionalAiInfo}
        />
      ) : null}
    </Flex>
  );
});
